// Public uploads can lack a Firebase download token. In that case retrieve the
// file through the existing staff endpoint, which checks submission access.
export function createReviewFileResolver({ storageUrl, download, urls = URL }) {
  const pending = new Map();
  const blobs = new Set();
  let generation = 0;
  return {
    async resolve(file) {
      const path = String(file?.objectPath || "").trim();
      if (!path) throw new Error("The document has no storage reference.");
      if (file.downloadURL && !file.downloadURL.startsWith("blob:")) return file.downloadURL;
      if (!pending.has(path)) {
        const currentGeneration = generation;
        const request = (async () => {
          try { return await storageUrl(path); } catch (_) { /* Use staff-authorized access. */ }
          const response = await download(path);
          if (!response.ok) {
            if (response.status === 404) throw new Error("The uploaded file could not be found in storage.");
            if ([401, 403].includes(response.status)) throw new Error("You do not have access to open this document. Please check your sign-in and permissions.");
            throw new Error("Unable to load this document. Please try again.");
          }
          const body = await response.blob();
          if (generation !== currentGeneration) return null;
          // The download endpoint sends application/octet-stream; previews need
          // the original type so PDFs are displayed rather than downloaded.
          const type = file.contentType || (/\.pdf$/i.test(file.originalName || path) ? "application/pdf" : body.type);
          const url = urls.createObjectURL(new Blob([body], { type }));
          blobs.add(url);
          return url;
        })();
        pending.set(path, request);
        request.catch(() => { if (pending.get(path) === request) pending.delete(path); });
      }
      return pending.get(path);
    },
    dispose() {
      generation += 1;
      blobs.forEach(url => urls.revokeObjectURL(url));
      blobs.clear();
      pending.clear();
    },
  };
}

// Object URLs only live in this browser tab; never save them with a review.
export function reviewFilesForStorage(files) {
  return (files || []).map(({ previewError, ...file }) => ({
    ...file,
    ...(file.downloadURL?.startsWith("blob:") ? { downloadURL: null } : {}),
  }));
}
